import { AlertTriangle, Check, X } from "lucide-react";
import { useCustom } from "@refinedev/core";
import { useEffect, useRef, useState } from "react";

import { Combo } from "@/components/Combo";
import { ApiError, api } from "@/lib/api";
import { errorMessage } from "@/lib/format";
import { placeLabel } from "@/lib/places";
import type {
  PlaceCard,
  PlaceLine,
  Serial,
  SpisanieByBarcode,
  SpisanieCategories,
  SpisanieEdit,
} from "@/lib/providers";
import {
  MAX_QTY,
  type SpisanieOutcome,
  barcodeOutcome,
  clampQty,
  keepKey,
  refusalMessage,
  requestKey,
  serialOutcome,
} from "@/lib/spisanie";

/**
 * ЧТО СПИСЫВАЮТ — и это единственное, чем два экрана списания отличаются.
 *
 * Мишени две, потому что ДВЕРЕЙ ДВЕ, и различает их то, ЧЕМ НАЗВАНА ВЕЩЬ:
 *
 *     place    баркод и место — серию находит склад (`POST /api/spisanie/barcode`)
 *     serial   сама вещь, номер уже в руках     (`POST /api/spisanie`)
 *
 * Из этого следует всё остальное различие, и ни одно из них не косметическое:
 * у места вещей может быть несколько штук (поле «сколько»), у серии она ровно
 * одна; месту нужен ключ однократности, серии — нет.
 */
export type SpisanieAim =
  | { kind: "place"; place: PlaceCard; line: PlaceLine }
  | { kind: "serial"; item: Serial };

/**
 * ОКНО СПИСАНИЯ — «вещи здесь нет, снимите её с учёта» (задача владельца
 * 11.09.26: «добавь кнопку, чтобы можно было кликнуть и списать нужный
 * баркод»; 16.09.26 то же попросили на карточке серии).
 *
 * До него списать вещь через кабинет было НЕЛЬЗЯ вовсе: дверь `POST
 * /api/spisanie` существует с 06.09.26, но зовут её раннер и соседняя система,
 * а кладовщик, стоящий над коробом, до сих пор шёл в CRM руками.
 *
 * ОКНО ОДНО НА ОБА ЭКРАНА, а не копия на каждом. Списание необратимо, и слова,
 * которыми экран предупреждает и отчитывается, — половина операции: разойдись
 * две копии, один и тот же отказ читался бы на карточке места и на карточке
 * серии по-разному. То же решение, что у `MoveFeed` с тремя лентами и у
 * `SpisanieFeed` с тремя журналами, и по той же причине.
 *
 * ОКНО, А НЕ КЛИК ПО КНОПКЕ, и это не осторожность ради осторожности: списание
 * необратимо и уезжает в боевой учёт под подписью того, кто вошёл. Промах
 * пальцем по строке таблицы не должен значить «снять вещь с учёта».
 *
 * ТРИ ПОЛЯ, И КАЖДОЕ ОТВЕЧАЕТ НА СВОЙ ВОПРОС:
 *
 *     сколько      штук не нашлось — по умолчанию одна; у СЕРИИ поля нет вовсе
 *     причина      почему списываем — по умолчанию «Не нашли»
 *     комментарий  что сказать следующему, кто откроет эту серию
 *
 * Причины приезжают справочником CRM (`GET /api/spisanie/categories`), а не
 * стоят списком в экране: справочник расширяемый, и зашитая копия разошлась бы
 * с ним на первой новой строке. Умолчание приезжает оттуда же — умолчание окна
 * и умолчание двери это одно решение, и жить оно обязано в одном месте.
 *
 * КЛЮЧ ОДНОКРАТНОСТИ живёт ЗДЕСЬ, в окне, и переживает неудачную попытку.
 * Человек, не дождавшийся ответа, жмёт кнопку ещё раз — и без ключа этот
 * второй раз списал бы ВТОРУЮ вещь: первая к тому моменту уже ушла из короба.
 * Правило «когда ключ менять» вынесено в `lib/spisanie.ts` и проверено
 * тестом — глазами на живом складе такое не проверяют. Двери по НОМЕРУ СЕРИИ
 * ключ не нужен: вещь названа точно, повтор отвечает `already` и второй записи
 * не делает.
 */
export function SpisanieSheet({
  aim,
  onClose,
  onDone,
}: {
  aim: SpisanieAim;
  onClose: () => void;
  /** Списание состоялось — экран надо перечитать: склад уже другой. */
  onDone: () => void;
}) {
  // Мишень-место названа отдельно: ей одной принадлежат поле «сколько» и ключ
  // однократности, и без неё эти две вещи пришлось бы проверять в разметке.
  const byPlace = aim.kind === "place" ? aim : null;

  const [qty, setQty] = useState(1);
  const [cause, setCause] = useState("");
  const [comment, setComment] = useState("");
  // Ключ однократности запроса. Заводится на ОТКРЫТИЕ окна и держится, пока
  // ответа не было вовсе; см. `keepKey`. У двери по номеру серии его нет.
  const [key, setKey] = useState(() =>
    byPlace ? requestKey(byPlace.place.place_id, byPlace.line.barcode) : "",
  );
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");
  const [done, setDone] = useState<SpisanieOutcome | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const causes = useCustom<SpisanieCategories>({
    url: "/api/spisanie/categories",
    method: "get",
  });
  const list = causes.result.data?.categories ?? [];
  const fallback = causes.result.data?.default_id;

  // Умолчание причины приезжает из сервиса, поэтому ставится после ответа.
  // Выбор человека при этом не сбрасывается: условие смотрит на пустоту.
  useEffect(() => {
    if (!cause && fallback) setCause(String(fallback));
  }, [cause, fallback]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const view = aimView(aim);
  const top = byPlace ? Math.min(byPlace.line.units, MAX_QTY) : 1;

  async function send() {
    setBusy(true);
    setFailed("");
    // «Ответ пришёл» — это и успех, и отказ двери: в обоих случаях запрос дошёл
    // и записан под ключом. Ответа не было вовсе — ключ обязан остаться прежним.
    let answered = false;
    try {
      let reply: SpisanieOutcome;
      if (aim.kind === "place") {
        reply = barcodeOutcome(
          await api.post<SpisanieByBarcode>("/api/spisanie/barcode", {
            request_id: key,
            barcode: aim.line.barcode,
            place_id: aim.place.place_id,
            qty,
            category_id: Number(cause) || undefined,
            comment: comment.trim(),
          }),
        );
      } else {
        reply = serialOutcome(
          await api.post<SpisanieEdit>("/api/spisanie", {
            // Пакет из одной вещи — одиночного пути у этой двери нет
            // намеренно: список из одной серии это тот же список.
            items: [
              {
                serial: aim.item.serial,
                // Целевое СОСТОЯНИЕ, а не действие: 0 списать. Повтор с тем же
                // значением безобиден по построению — на этом стоит то, что
                // ключа однократности у двери нет вовсе.
                available: 0,
                category_id: Number(cause) || undefined,
                comment: comment.trim(),
                // Место НЕ ЗАДАЁМ: дверь считает его сама — «<ГОРОД> Списано»
                // того города, где вещь лежит СЕЙЧАС, под блокировкой строки.
                // Назови его экран, он назвал бы город, прочитанный до
                // операции, — а вещь между чтением и записью могли сдвинуть.
              },
            ],
          }),
          aim.item.serial,
        );
      }
      answered = true;
      setDone(reply);
      // Перечитывать — по признаку самого итога, а не по `ok`: обе двери
      // отвечают 200 и на частичную работу, и в одну сторону это «списалось не
      // всё, но склад уже другой», а в другую — «не списалось ничего».
      if (reply.reread) onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        answered = true;
        setFailed(refusalMessage(err.message));
      } else {
        setFailed(errorMessage(err));
      }
    } finally {
      if (byPlace && !keepKey(answered))
        setKey(requestKey(byPlace.place.place_id, byPlace.line.barcode));
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Списание"
      data-annot="окно списания"
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-full w-full max-w-[460px] flex-col gap-3 overflow-y-auto rounded-[var(--radius-card)] border border-border bg-bg-elevated p-4"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-[14px] font-semibold">
            {view.title}
            {view.city ? (
              <span className="ml-1.5 text-[12.5px] font-normal text-fg-muted">
                {view.city}
              </span>
            ) : null}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            data-annot="закрыть"
            className="flex h-7 w-7 flex-none items-center justify-center rounded-[var(--radius-ui)] border border-border text-fg-muted hover:text-fg"
          >
            <X size={14} />
          </button>
        </div>

        {/* ЧТО СПИСЫВАЕМ — картинкой и баркодом. Имя товара короб не различает
            (у пяти позиций одного короба оно дословно одно и то же), а человек
            стоит над коробом с вещью в руках. */}
        <div
          data-annot="товар"
          className="flex items-center gap-3 rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-2"
        >
          <div className="flex h-[54px] w-[40px] flex-none items-center justify-center overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg text-[10px] text-fg-muted">
            {view.image ? (
              <img
                src={view.image}
                alt={view.name}
                referrerPolicy="no-referrer"
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="tnum">{view.stub}</span>
            )}
          </div>
          <div className="min-w-0">
            <p className="tnum text-[13px] font-medium">{view.head}</p>
            <p className="truncate text-[12.5px] text-fg-muted">{view.name}</p>
            <p className="text-[12px] text-fg-muted">{view.note}</p>
          </div>
        </div>

        {done ? (
          <Result outcome={done} onClose={onClose} />
        ) : (
          <>
            {/* «Сколько» есть только там, где позиция считается ШТУКАМИ. У
                серии вещь одна физически, и поле спрашивало бы о том, на что
                ответ уже известен. */}
            {byPlace ? (
              <label className="flex items-center justify-between gap-3 text-[13px]">
                <span>Сколько списать</span>
                <span className="flex items-center gap-2">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={top}
                    value={qty}
                    onChange={(e) =>
                      setQty(clampQty(Number(e.target.value), byPlace.line.units))
                    }
                    data-annot="сколько"
                    className="tnum h-8 w-[70px] rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-right"
                  />
                  <span className="text-[12px] text-fg-muted">из {top}</span>
                </span>
              </label>
            ) : null}

            <div className="flex items-center justify-between gap-3 text-[13px]">
              <span>Причина</span>
              {causes.query.isFetching && list.length === 0 ? (
                <span className="text-[12.5px] text-fg-muted">загружаю…</span>
              ) : (
                <Combo
                  label="причина"
                  value={cause}
                  options={list.map((one) => ({
                    value: String(one.category_id),
                    label: one.name,
                  }))}
                  onChange={setCause}
                  width={230}
                />
              )}
            </div>

            <label className="flex flex-col gap-1 text-[13px]">
              <span>Комментарий</span>
              <input
                type="text"
                value={comment}
                maxLength={300}
                onChange={(e) => setComment(e.target.value)}
                placeholder="что сказать следующему, кто откроет эту серию"
                data-annot="комментарий"
                className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px]"
              />
            </label>

            {/* Сказано вслух, и это не украшение: списание необратимо, уезжает
                в БОЕВОЙ учёт и подписывается тем, кто вошёл. */}
            <p className="flex items-start gap-2 rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-2.5 py-2 text-[12px] text-fg-muted">
              <AlertTriangle size={14} className="mt-px flex-none" />
              <span>
                Вещь уйдёт с учёта в «{view.city || "город"} Списано» под вашей
                подписью. Отменить это можно только снятием списания в CRM.
              </span>
            </p>

            {failed ? (
              <p
                role="alert"
                data-annot="отказ"
                className="rounded-[var(--radius-ui)] border border-err/60 bg-err-bg px-2.5 py-2 text-[12.5px]"
              >
                {failed}
              </p>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={send}
                disabled={busy || !cause}
                data-annot="списать"
                className="h-8 rounded-[var(--radius-ui)] border border-err/60 bg-err-bg px-3 text-[13px] font-medium hover:bg-err-bg/70 disabled:opacity-55"
              >
                {busy
                  ? "Списываю…"
                  : byPlace
                    ? `Списать ${qty} шт`
                    : "Списать вещь"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Как окно называет вещь и место — всё, чем мишени отличаются НА ЭКРАНЕ. */
type AimView = {
  title: string;
  city: string | null;
  image: string;
  /** Чем подписана пустая рамка, когда фотографии нет. */
  stub: string;
  head: string;
  name: string;
  note: string;
};

function aimView(aim: SpisanieAim): AimView {
  if (aim.kind === "place") {
    const { place, line } = aim;
    return {
      title: `Списать из ${placeLabel(place)}`,
      city: place.city,
      image: line.image_url,
      stub: String(line.model_id),
      head: line.barcode,
      name: `${line.size ? `${line.size} · ` : ""}${line.name || `Товар ${line.model_id}`}`,
      note: `по учёту здесь ${line.units} шт`,
    };
  }

  const { item } = aim;
  const card = item.card;
  // Имя МЕСТА берётся так же, как в блоке «Где лежит» той же карточки: правила
  // `placeLabel` здесь применить не к чему — у места серии нет ни номера
  // короба, ни признака `korob`, а называть одну и ту же точку склада на
  // соседних половинах экрана по-разному нельзя.
  const place = item.place;
  const where = place ? place.name || `Место ${place.place_id}` : null;
  return {
    title: `Списать серию ${item.serial}`,
    city: place?.city ?? null,
    image: card?.image_url ?? "",
    stub: String(item.serial),
    // Крупно — БАРКОД, а не номер серии: номер уже стоит в заголовке окна, а
    // в руках у человека ярлык. Карточки маркетплейса у вещи может не быть
    // вовсе — тогда её нечем назвать, и молчать об этом нельзя.
    head: card?.barcode || "без карточки маркетплейса",
    name:
      [card?.size || item.size, card?.name].filter(Boolean).join(" · ") ||
      `Артикул ${item.article}`,
    note: where ? `лежит в ${where}` : "места у серии нет",
  };
}

/**
 * Что получилось. Номера названы вслух намеренно: списание необратимо, и номер
 * ушедшей с учёта вещи (или её строки списания) — единственное, чем человек
 * проверит нас потом.
 *
 * Форма итога ОДНА на обе двери (`lib/spisanie.ts::SpisanieOutcome`): ответы у
 * них разные, а слова, которыми экран отчитывается о необратимой операции,
 * обязаны быть одни.
 */
function Result({
  outcome,
  onClose,
}: {
  outcome: SpisanieOutcome;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col gap-2" data-annot="итог">
      <p
        className={
          "flex items-start gap-2 rounded-[var(--radius-ui)] border px-2.5 py-2 text-[13px] " +
          (outcome.ok ? "border-border bg-bg-subtle" : "border-err/60 bg-err-bg")
        }
      >
        {outcome.ok ? <Check size={15} className="mt-px flex-none" /> : null}
        <span>
          {outcome.message}
          {outcome.note ? (
            <span className="block text-[12px] text-fg-muted">
              {outcome.note}
            </span>
          ) : null}
        </span>
      </p>

      {outcome.bad.length > 0 ? (
        <ul className="flex flex-col gap-1 text-[12.5px]">
          {outcome.bad.map((one) => (
            <li key={`bad-${one.serial}`} className="text-fg-muted">
              <span className="tnum">{one.serial}</span>:{" "}
              {refusalMessage(one.error ?? "", one.detail)}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          data-annot="закрыть итог"
          className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover"
        >
          Закрыть
        </button>
      </div>
    </div>
  );
}
